/**
 * CustomerNotesPanel - Reusable notes component for all entity views
 * Displays and manages customer notes with full CRUD operations
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  List,
  Input,
  Button,
  Tag,
  Space,
  Select,
  Typography,
  Popconfirm,
  Tooltip,
  Modal,
  Form,
  Checkbox,
  message,
  Alert,
  Dropdown,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PushpinOutlined,
  SearchOutlined,
  TagOutlined,
  ReloadOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import { useTheme, useMediaQuery } from "@mui/material";
import api from "@/shared/api";
import { logError, logInfo } from "@/shared/utils/logger.js";

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/**
 * CustomerNotesPanel Component
 * @param {string} customerId - Customer ID (required)
 * @param {string} context - Context filter ('lead', 'quote', 'order', 'booking', 'followup', 'invoice', 'general')
 * @param {string} relatedId - Related entity ID (leadId, quoteId, orderId, etc.)
 * @param {boolean} autoContext - Automatically set context based on relatedId type
 * @param {string} entityType - Entity type for auto-detection ('lead', 'quote', etc.)
 */
const CustomerNotesPanel = ({
  customerId,
  context,
  relatedId,
  autoContext = false,
  entityType = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    context: context || "all",
    search: "",
    priority: "all",
    tags: [],
    flowId: null,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [form] = Form.useForm();
  const [error, setError] = useState(null);

  // Auto-detect context if not provided
  useEffect(() => {
    if (autoContext && entityType && !context) {
      setFilters((prev) => ({ ...prev, context: entityType }));
    }
  }, [autoContext, entityType, context]);

  useEffect(() => {
    if (customerId) {
      fetchNotes();
    } else {
      setError("Customer ID is required");
    }
  }, [customerId, filters]);

  const fetchNotes = async () => {
    if (!customerId) return;

    setLoading(true);
    setError(null);
    try {
      const params = {
        ...(filters.context !== "all" && { context: filters.context }),
        ...(filters.search && { search: filters.search }),
        ...(filters.priority !== "all" && { priority: filters.priority }),
        ...(filters.flowId && { flowId: filters.flowId }),
        includeArchived: false,
      };

      const response = await api.get(`/customers/${customerId}/notes`, {
        params,
      });

      // Defensive checks: ensure response exists and is an object
      if (!response || typeof response !== "object") {
        logError("Failed to fetch notes - invalid response", {
          customerId,
          responseType: typeof response,
          response,
        });
        setError("Invalid response from server");
        setNotes([]);
        return;
      }

      // Handle successful response
      if (response.success === true) {
        // Ensure response.data is always an array, even if null/undefined
        const notesData = Array.isArray(response.data) ? response.data : [];
        setNotes(notesData);
        logInfo("Notes fetched successfully", {
          customerId,
          count: notesData.length,
        });
      }
      // Handle error response structure
      else if (response.success === false) {
        const errorMessage = response.error || response.message || "Failed to load notes";
        setError(errorMessage);
        setNotes([]); // Ensure notes is always an array
        logError("Failed to fetch notes", {
          customerId,
          error: errorMessage,
          response,
        });
      }
      // Handle unexpected response structure (fallback)
      else {
        // If response doesn't have success property, check if data exists
        if (Array.isArray(response.data)) {
          // Response might be { data: [...] } without success property
          setNotes(response.data);
          logInfo("Notes fetched successfully (fallback)", {
            customerId,
            count: response.data.length,
          });
        } else if (Array.isArray(response)) {
          // Response might be an array directly
          setNotes(response);
          logInfo("Notes fetched successfully (direct array)", {
            customerId,
            count: response.length,
          });
        } else {
          // Unknown response structure
          logError("Failed to fetch notes - unexpected response structure", {
            customerId,
            response,
            responseKeys: response ? Object.keys(response) : null,
          });
          setError("Unexpected response format");
          setNotes([]);
        }
      }
    } catch (error) {
      // Enhanced error handling with user-friendly messages
      const errorMessage = error?.message || error?.toString() || "Failed to load notes";
      
      // Handle 404 errors (customer not found) gracefully
      if (errorMessage.includes("404") || errorMessage.includes("Customer not found") || errorMessage.includes("CUSTOMER_NOT_FOUND")) {
        // Customer doesn't exist - show empty state instead of error
        setError(null);
        setNotes([]);
        logInfo("Customer not found, showing empty notes", {
          customerId,
        });
      } else {
        // Other errors - show user-friendly message
        const friendlyMessage = errorMessage.includes("timeout")
          ? "Request timed out. Please try again."
          : errorMessage.includes("500")
          ? "Server error. Please try again later."
          : errorMessage.includes("network")
          ? "Network error. Please check your connection."
          : "Unable to load notes. Please try again.";
        
        setError(friendlyMessage);
        setNotes([]); // Always ensure notes is an array
        logError("Failed to fetch notes - exception", {
          customerId,
          error: errorMessage,
          errorType: error?.constructor?.name,
          errorStack: error?.stack,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (values) => {
    try {
      const response = await api.post(`/customers/${customerId}/notes`, {
        ...values,
        context: values.context || context || "general",
        relatedId: relatedId || null,
      });

      if (response.success) {
        message.success("Note added successfully");
        logInfo("Note added", {
          customerId,
          noteId: response.data.id,
        });
        setModalVisible(false);
        form.resetFields();
        fetchNotes();
      }
    } catch (error) {
      message.error("Failed to add note");
      logError("Failed to add note", {
        customerId,
        error: error.message,
      });
    }
  };

  const handleUpdateNote = async (noteId, updates) => {
    try {
      const response = await api.put(
        `/customers/${customerId}/notes/${noteId}`,
        updates
      );

      if (response.success) {
        message.success("Note updated successfully");
        logInfo("Note updated", {
          customerId,
          noteId,
        });
        fetchNotes();
      }
    } catch (error) {
      message.error("Failed to update note");
      logError("Failed to update note", {
        customerId,
        noteId,
        error: error.message,
      });
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const response = await api.delete(
        `/customers/${customerId}/notes/${noteId}`
      );

      if (response.success) {
        message.success("Note deleted successfully");
        logInfo("Note deleted", {
          customerId,
          noteId,
        });
        fetchNotes();
      }
    } catch (error) {
      message.error("Failed to delete note");
      logError("Failed to delete note", {
        customerId,
        noteId,
        error: error.message,
      });
    }
  };

  const handlePinNote = (noteId, isPinned) => {
    handleUpdateNote(noteId, { isPinned: !isPinned });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "default",
      normal: "blue",
      high: "orange",
      urgent: "red",
    };
    return colors[priority] || "default";
  };

  const getContextColor = (context) => {
    const colors = {
      general: "default",
      lead: "blue",
      quote: "green",
      order: "purple",
      booking: "cyan",
      followup: "orange",
      invoice: "magenta",
    };
    return colors[context] || "default";
  };

  const openAddNoteModal = () => {
    setEditingNote(null);
    form.resetFields();
    form.setFieldsValue({
      context: context || "general",
      priority: "normal",
    });
    setModalVisible(true);
  };

  const noteModal = (
    <Modal
      title={editingNote ? "Edit Note" : "Add Note"}
      open={modalVisible}
      onCancel={() => {
        setModalVisible(false);
        setEditingNote(null);
        form.resetFields();
      }}
      footer={null}
      width={isMobile ? "95%" : 600}
      style={isMobile ? { top: 20 } : undefined}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          if (editingNote) {
            handleUpdateNote(editingNote.id, values);
          } else {
            handleAddNote(values);
          }
          setModalVisible(false);
        }}
      >
        <Form.Item
          name="content"
          label="Note Content"
          rules={[{ required: true, message: "Note content is required" }]}
        >
          <TextArea rows={4} placeholder="Enter note content..." />
        </Form.Item>

        <Form.Item
          name="context"
          label="Context"
          initialValue={context || "general"}
        >
          <Select>
            <Option value="general">General</Option>
            <Option value="lead">Lead</Option>
            <Option value="quote">Quote</Option>
            <Option value="order">Order</Option>
            <Option value="booking">Booking</Option>
            <Option value="followup">Follow-up</Option>
            <Option value="invoice">Invoice</Option>
          </Select>
        </Form.Item>

        <Form.Item name="priority" label="Priority" initialValue="normal">
          <Select>
            <Option value="low">Low</Option>
            <Option value="normal">Normal</Option>
            <Option value="high">High</Option>
            <Option value="urgent">Urgent</Option>
          </Select>
        </Form.Item>

        <Form.Item name="tags" label="Tags">
          <Select
            mode="tags"
            placeholder="Add tags (press Enter to add)"
            tokenSeparators={[","]}
          />
        </Form.Item>

        <Form.Item name="isPinned" valuePropName="checked">
          <Checkbox>Pin this note</Checkbox>
        </Form.Item>

        <Form.Item>
          <Space
            style={{
              width: "100%",
              justifyContent: isMobile ? "stretch" : "flex-end",
            }}
          >
            <Button
              type="primary"
              htmlType="submit"
              block={isMobile}
              size={isMobile ? "large" : "middle"}
            >
              {editingNote ? "Update" : "Add"} Note
            </Button>
            <Button
              onClick={() => setModalVisible(false)}
              block={isMobile}
              size={isMobile ? "large" : "middle"}
            >
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );

  const addNoteButton = (
    <Button
      type="primary"
      icon={<PlusOutlined />}
      onClick={openAddNoteModal}
      size={isMobile ? "small" : "middle"}
    >
      {isMobile ? null : "Add Note"}
    </Button>
  );

  if (!customerId) {
    return (
      <Card>
        <Alert
          message="Customer Notes"
          description="Customer ID is required to display notes"
          type="warning"
        />
      </Card>
    );
  }

  const hasNotes = Array.isArray(notes) && notes.length > 0;

  // No notes yet: hide the panel, keep create button visible
  if (!hasNotes) {
    return (
      <Space
        direction="vertical"
        style={{ marginBottom: isMobile ? 8 : 16, width: "100%" }}
      >
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            closable
            onClose={() => setError(null)}
          />
        )}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openAddNoteModal}
          loading={loading}
          size={isMobile ? "small" : "middle"}
        >
          {isMobile ? null : "Add Note"}
        </Button>
        {noteModal}
      </Space>
    );
  }

  return (
    <Card
      title={
        <Space>
          <Title level={5} style={{ margin: 0, fontSize: isMobile ? 16 : undefined }}>
            Customer Notes
          </Title>
          <Tag>{Array.isArray(notes) ? notes.length : 0}</Tag>
        </Space>
      }
      extra={
        <Space size={isMobile ? 4 : 8}>
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchNotes}
              loading={loading}
              size={isMobile ? "small" : "middle"}
            />
          </Tooltip>
          {addNoteButton}
        </Space>
      }
      style={{
        marginBottom: isMobile ? 8 : 16,
      }}
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Filters */}
      <Space
        direction={isMobile ? "vertical" : "horizontal"}
        style={{
          marginBottom: isMobile ? 12 : 16,
          width: "100%",
          display: "flex",
        }}
        size={isMobile ? 8 : 8}
      >
        <Input
          placeholder="Search notes..."
          prefix={<SearchOutlined />}
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ width: isMobile ? "100%" : 200 }}
          allowClear
          size={isMobile ? "small" : "middle"}
        />
        <Select
          value={filters.context}
          onChange={(value) => setFilters({ ...filters, context: value })}
          style={{ width: isMobile ? "100%" : 150 }}
          size={isMobile ? "small" : "middle"}
        >
          <Option value="all">All Contexts</Option>
          <Option value="general">General</Option>
          <Option value="lead">Lead</Option>
          <Option value="quote">Quote</Option>
          <Option value="order">Order</Option>
          <Option value="booking">Booking</Option>
          <Option value="followup">Follow-up</Option>
          <Option value="invoice">Invoice</Option>
        </Select>
        <Select
          value={filters.priority}
          onChange={(value) => setFilters({ ...filters, priority: value })}
          style={{ width: isMobile ? "100%" : 120 }}
          size={isMobile ? "small" : "middle"}
        >
          <Option value="all">All Priorities</Option>
          <Option value="low">Low</Option>
          <Option value="normal">Normal</Option>
          <Option value="high">High</Option>
          <Option value="urgent">Urgent</Option>
        </Select>
      </Space>

      {/* Notes List */}
      <List
        loading={loading}
        dataSource={notes}
        renderItem={(note) => {
          if (!note || typeof note !== "object") {
            return null;
          }
          const actionMenuItems = [
            {
              key: "pin",
              label: note.isPinned ? "Unpin" : "Pin",
              icon: <PushpinOutlined />,
              onClick: () => handlePinNote(note.id, note.isPinned),
            },
            {
              key: "edit",
              label: "Edit",
              icon: <EditOutlined />,
              onClick: () => {
                setEditingNote(note);
                form.setFieldsValue(note);
                setModalVisible(true);
              },
            },
            {
              key: "delete",
              label: "Delete",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => {
                Modal.confirm({
                  title: "Delete this note?",
                  content: "This action cannot be undone.",
                  okText: "Delete",
                  okType: "danger",
                  cancelText: "Cancel",
                  onOk: () => handleDeleteNote(note.id),
                });
              },
            },
          ];

          return (
            <List.Item
              style={{
                backgroundColor: note.isPinned ? "#fffbe6" : "transparent",
                borderLeft: note.isPinned ? "3px solid #faad14" : "none",
                padding: isMobile ? "12px 8px" : "12px 16px",
                marginBottom: isMobile ? 12 : 8,
                borderRadius: 4,
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}
              actions={
                isMobile
                  ? [
                      <div key="actions" onClick={(e) => e.stopPropagation()}>
                        <Dropdown
                          menu={{ items: actionMenuItems }}
                          trigger={["click"]}
                        >
                          <Button
                            type="text"
                            icon={<MoreOutlined />}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Dropdown>
                      </div>,
                    ]
                  : [
                      <Tooltip key="pin" title={note.isPinned ? "Unpin" : "Pin"}>
                        <Button
                          type="text"
                          icon={<PushpinOutlined />}
                          onClick={() => handlePinNote(note.id, note.isPinned)}
                          size="small"
                        />
                      </Tooltip>,
                      <Tooltip key="edit" title="Edit">
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditingNote(note);
                            form.setFieldsValue(note);
                            setModalVisible(true);
                          }}
                          size="small"
                        />
                      </Tooltip>,
                      <Popconfirm
                        key="delete"
                        title="Delete this note?"
                        onConfirm={() => handleDeleteNote(note.id)}
                      >
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          size="small"
                        />
                      </Popconfirm>,
                    ]
              }
            >
              <List.Item.Meta
                title={
                  <Space wrap size={isMobile ? 4 : 8}>
                    {note.isPinned && (
                      <PushpinOutlined
                        style={{
                          color: "#faad14",
                          fontSize: isMobile ? 12 : 14,
                        }}
                      />
                    )}
                    <Text
                      strong
                      ellipsis={
                        isMobile
                          ? { tooltip: note.content || "No content" }
                          : false
                      }
                      style={{
                        fontSize: isMobile ? 14 : undefined,
                        maxWidth: isMobile
                          ? "calc(100vw - 120px)"
                          : undefined,
                      }}
                    >
                      {note.content || "No content"}
                    </Text>
                    {note.context && (
                      <Tag
                        color={getContextColor(note.context)}
                        style={{ margin: 0 }}
                      >
                        {note.context}
                      </Tag>
                    )}
                    {note.priority && (
                      <Tag
                        color={getPriorityColor(note.priority)}
                        style={{ margin: 0 }}
                      >
                        {note.priority}
                      </Tag>
                    )}
                  </Space>
                }
                description={
                  <Space
                    direction="vertical"
                    size="small"
                    style={{
                      width: "100%",
                      marginTop: isMobile ? 8 : 4,
                    }}
                  >
                    {Array.isArray(note.tags) && note.tags.length > 0 && (
                      <Space wrap size={isMobile ? 4 : 8}>
                        {note.tags.map((tag, idx) => (
                          <Tag
                            key={idx}
                            icon={<TagOutlined />}
                            color="blue"
                            style={{ margin: 0 }}
                          >
                            {tag}
                          </Tag>
                        ))}
                      </Space>
                    )}
                    <Text
                      type="secondary"
                      style={{ fontSize: isMobile ? 11 : 12 }}
                    >
                      {note.createdByName || "Unknown"} •{" "}
                      {note.createdAt
                        ? format(
                            new Date(note.createdAt),
                            isMobile
                              ? "MMM dd, HH:mm"
                              : "MMM dd, yyyy HH:mm"
                          )
                        : "Unknown date"}
                      {note.updatedAt &&
                        note.updatedAt !== note.createdAt &&
                        " (edited)"}
                    </Text>
                  </Space>
                }
              />
            </List.Item>
          );
        }}
      />

      {noteModal}
    </Card>
  );
};

export default CustomerNotesPanel;

